import { useMemo } from 'react';
import { Badge } from '@givebutter/primitives/badge';
import * as Meter from '@givebutter/primitives/meter';
import { maskWholeNumber } from '@givebutter/util';
import { faUser, faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { twMerge } from 'tailwind-merge';

const NUM_CONTACTS_PER_OVERAGE_PACK = 100;

const SubscriptionUsage = () => {
    const { validGivebutterPlusPlan } = useCurrentAccount();
    const { planUsagePercentage, overageUsage, planWidth } = useBarValues();

    if (!validGivebutterPlusPlan) {
        return null;
    }

    const { current_contacts_count, max_contacts } = validGivebutterPlusPlan.tier;
    const totalPercentage = Math.round((current_contacts_count / max_contacts) * 100);

    const usesOverages = overageUsage > 0;

    return (
        <div className="flex w-full flex-col gap-3">
            <div className="flex items-center gap-4">
                <p className="font-medium">Usage</p>
                <UsageBadge percentage={totalPercentage} />
            </div>
            <div className="flex flex-col gap-2">
                <div className="text-sm">
                    <p>
                        <span className="font-medium">
                            {maskWholeNumber(current_contacts_count)}
                        </span>{' '}
                        / {maskWholeNumber(max_contacts)}
                    </p>
                    <p className="text-gray-700">Contacts in your plan</p>
                </div>

                <div className="flex">
                    <div className="shrink-0" style={{ width: `${planWidth}%` }}>
                        <Meter.Root
                            type="line"
                            value={Math.min(planUsagePercentage, 100)}
                            aria-label="Percentage of Contact Limit Used"
                            variant="success"
                        >
                            <Meter.LineTrack
                                className={twMerge(
                                    'rounded-r-none bg-gray-100',
                                    !usesOverages && 'rounded-full',
                                )}
                            >
                                <Meter.LineIndicator className="rounded-r-none" />
                            </Meter.LineTrack>
                        </Meter.Root>
                    </div>
                    {usesOverages && (
                        <div className="w-full">
                            <Meter.Root
                                type="line"
                                value={overageUsage}
                                aria-label="Percentage of Overage Packs Used"
                                variant="error"
                            >
                                <Meter.LineTrack className="rounded-l-none bg-gray-100">
                                    <Meter.LineIndicator className="rounded-none" />
                                </Meter.LineTrack>
                            </Meter.Root>
                        </div>
                    )}
                </div>

                <div className="relative flex justify-between">
                    <FontAwesomeIcon icon={faUser} size="xs" className="text-gray-400" />
                    {overageUsage > 0 && (
                        <p
                            className="absolute -translate-x-1/2 text-xs text-gray-500"
                            style={{
                                left: `${planWidth}%`,
                            }}
                        >
                            {max_contacts}
                        </p>
                    )}
                    <FontAwesomeIcon icon={faUserGroup} size="xs" className="text-gray-400" />
                </div>
            </div>
        </div>
    );
};

const useBarValues = () => {
    const { validGivebutterPlusPlan } = useCurrentAccount();
    const { tier, is_reporting_contact_overage } = validGivebutterPlusPlan ?? {};

    const [planUsagePercentage, overageUsage, planWidth] = useMemo(() => {
        if (!tier) {
            return [0, 0, 0];
        }
        const {
            max_contacts,
            current_contacts_count,
            overage_pack_count,
            unreported_overage_pack_count,
        } = tier;

        const isGracePeriod = !is_reporting_contact_overage;
        const numPacks = isGracePeriod ? unreported_overage_pack_count : overage_pack_count;
        const barMax = max_contacts + numPacks * NUM_CONTACTS_PER_OVERAGE_PACK;

        const planUsagePercentage = Math.min((current_contacts_count / max_contacts) * 100, 100);

        const overageUsage =
            numPacks === 0
                ? 0
                : ((current_contacts_count - max_contacts) /
                      (numPacks * NUM_CONTACTS_PER_OVERAGE_PACK)) *
                  100;

        const planWidth =
            current_contacts_count <= max_contacts
                ? 100
                : (Math.min(current_contacts_count, max_contacts) / barMax) * 100;

        return [planUsagePercentage, overageUsage, planWidth];
    }, [tier, is_reporting_contact_overage]);

    return {
        planUsagePercentage,
        overageUsage,
        planWidth,
    };
};

interface UsageBadgeProps {
    percentage: number;
}
const UsageBadge = ({ percentage }: UsageBadgeProps) => {
    const badgeColor = useMemo(() => {
        if (percentage >= 100) return 'error';
        if (percentage >= 75) return 'warning';
        return 'success';
    }, [percentage]);

    return (
        <Badge size="sm" color={badgeColor}>
            {percentage}% used
        </Badge>
    );
};

export { SubscriptionUsage };
